import { ApiClientInput } from "./type";
import client from "./apiClient";
import { getResolvedString } from "../utils";
import { HttpVerb } from "shared/utils/enums";
import { MutationFunction } from "react-query/types/core/types";

/**
 * @name clientWrapper
 * @description This is the api client wrapper. We can use this to make api calls.
 * @example `
   return useQuery(
     [queryKey, uuId | AnyObject],
     clientWrapper<type of response>(apiUrl),
     {useQuery config options}
     );
 * `
 * @param path
 * @param params
 */
export default function clientWrapper(path: string, params?: ApiClientInput) {
  const resolvedPath = params?.pathParams ? getResolvedString(path, params?.pathParams) : path;
  return (context: any) => {
    let apiUrl;
    const inputParam = context.queryKey[1];
    switch (typeof inputParam) {
      case "string":
        const keysList = detectKeysFromString(resolvedPath);
        if (keysList.length > 0) {
          const valObj = keysList.reduce((prev: object, curr: string) => {
            return { ...prev, [curr]: inputParam };
          }, {});
          apiUrl = getResolvedString(resolvedPath, valObj);
        } else {
          apiUrl = resolvedPath;
        }
        break;
      case "object":
        const dataObj = Object.keys(inputParam).reduce((prevVal: object, currVal: string) => {
          return { ...prevVal, [currVal]: inputParam[currVal] };
        }, {});
        apiUrl = getResolvedString(resolvedPath, dataObj);
        break;
      default:
        apiUrl = resolvedPath;
    }
    return client(apiUrl, params);
  };
}

export function clientPostWrapper<ResponseType, RequestType = ResponseType>(
  path: string,
  clientConfig?: ApiClientInput
): MutationFunction<ResponseType, RequestType> {
  const resolvedPath = clientConfig?.pathParams ? getResolvedString(path, clientConfig?.pathParams) : path;
  return (body: RequestType) => {
    return client<ResponseType>(resolvedPath, { ...clientConfig, body: body }) as unknown as Promise<ResponseType>;
  };
}

function detectKeysFromString(str: string): Array<string> {
  if (!str) {
    return [];
  }
  const matchedKeys = str.match(/{[a-zA-Z0-9]*}/g) || [];
  return matchedKeys.map(rawKey => {
    return rawKey.substring(1, rawKey.length - 1);
  });
}

export function clientMutateWrapper<T>(path: string, method: HttpVerb): MutationFunction<T, ApiClientInput> {
  return (clientConfig?: ApiClientInput) => {
    const resolvedPath = clientConfig?.pathParams ? getResolvedString(path, clientConfig?.pathParams) : path;
    return client<T>(resolvedPath, {
      ...clientConfig,
      method: method
    }) as unknown as Promise<T>;
  };
}
